// Video language support ("English" / "Español").
//
// Language lives on the catalog row as a `language` field ("en" | "es"). The
// video-data table is schemaless -- POST /add-video spreads the request body
// straight into DynamoDB -- so this needed no backend change and no backfill:
// a row without the field reads as English, which is what every pre-existing
// video is.
//
// The learner's choice is a display preference, not account state, so it stays
// in localStorage per device. Switching languages never writes to DynamoDB and
// so never touches the reporting stream.

import ES_LABELS from "./labels.es";
import ES_UI from "./ui.es";

export const DEFAULT_LANGUAGE = "en";

export const LANGUAGES = [
  {code: "en", label: "English", short: "EN"},
  {code: "es", label: "Español", short: "ES"},
];

const LANGUAGE_KEY = "videoLanguage";

// Escape hatch for demoing the switch against a catalog that has no Spanish
// rows yet. Normally false: the toggle is driven by the DATA, appearing only
// when the catalog actually holds more than one language, so an English-only
// environment is untouched by this feature and no participant is offered a
// language with nothing behind it.
export const ALWAYS_SHOW_LANGUAGE_TOGGLE = false;

/**
 * The language a video is in. Tolerates the shapes an admin might type or a
 * feed might carry ("es", "ES", "es-MX", "Spanish", "Español"). Anything
 * unrecognised falls back to English rather than to a code nothing matches --
 * a typo in the upload form must never hide a video from every learner.
 */
export const getVideoLanguage = (video) => {
  const raw = String(video?.language ?? "")
    .trim()
    .toLowerCase();
  if (!raw) return DEFAULT_LANGUAGE;
  // Exact code, a regional variant ("es-MX"), or the language spelled out.
  // Deliberately NOT a bare "es" prefix: that also swallows "ESL", which an
  // education catalog can plausibly carry, and would hide such a video from
  // every English reader while surfacing it to Spanish ones.
  if (
    raw === "es" ||
    raw.startsWith("es-") ||
    raw.startsWith("spanish") ||
    raw.startsWith("español") ||
    raw.startsWith("espanol")
  ) {
    return "es";
  }
  return DEFAULT_LANGUAGE;
};

/** Videos in one language. */
export const filterByLanguage = (videos, code) =>
  (videos || []).filter((v) => getVideoLanguage(v) === code);

/**
 * Which of LANGUAGES actually appear in the catalog, in LANGUAGES order. The
 * dashboard only renders the toggle when this has more than one entry, so an
 * environment with English-only content shows no control at all.
 */
export const availableLanguages = (videos) => {
  const present = new Set((videos || []).map(getVideoLanguage));
  return LANGUAGES.filter((l) => present.has(l.code));
};

/** The learner's stored choice, or English. */
export const getPreferredLanguage = () => {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_KEY);
    return LANGUAGES.some((l) => l.code === stored) ? stored : DEFAULT_LANGUAGE;
  } catch {
    // Private mode / storage disabled -- English is the safe fallback.
    return DEFAULT_LANGUAGE;
  }
};

/** Persist the choice. Best-effort: an unwritable store just means it resets. */
export const setPreferredLanguage = (code) => {
  try {
    window.localStorage.setItem(LANGUAGE_KEY, code);
  } catch {
    /* storage unavailable -- the in-memory choice still applies this session */
  }
};

// --- Display labels --------------------------------------------------------
//
// The translation tables live in their own labels.<code>.js files; this module
// only decides which one applies. See labels.es.js for why translating is
// display-only and must never touch the stored category.
const LABEL_TABLES = {es: ES_LABELS};

// The app's own fixed copy, kept in its own table because it translates the
// chrome rather than stored catalog values. See ui.es.js.
const UI_TABLES = {es: ES_UI};

/**
 * The label to show for a category / section / playlist name in the chosen
 * language. Falls back to the original text when there is no translation, so
 * a new category added by an admin still renders rather than disappearing.
 */
export const displayLabel = (label, language = DEFAULT_LANGUAGE) => {
  const table = LABEL_TABLES[language];
  if (!table) return label;
  return table[String(label ?? "").trim()] ?? label;
};

/**
 * Fixed UI copy in the chosen language, keyed by the English string so an
 * untranslated entry renders in English rather than blank. Variables are
 * composed onto the end by the caller -- see ui.es.js for why that is safe.
 */
export const uiText = (text, language = DEFAULT_LANGUAGE) => {
  const table = UI_TABLES[language];
  if (!table) return text;
  return table[String(text ?? "").trim()] ?? text;
};
